/**
 * Time capture building blocks (T&B-1 PR 2): the client picker, the billing
 * job + task picker, the entry form, the Log time dialog and the top-bar
 * timer.
 *
 * None of these decides a rule. Steps of 0.05 hours, no future work, a client
 * on chargeable work, no client on leave, 24 hours a day at most -- the server
 * (and under it the database) refuses, and the message is shown as it comes
 * back. The pickers only avoid offering what is certain to be refused: an
 * archived job, a leave job on the timer.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  api,
  type BillingJob,
  type ClientListRow,
  type ClientRef,
  type EntryBody,
  type TimeEntryRow,
  type TimerState,
} from '../api'

export const inputCls = 'rounded-eq-sm border border-eq-border px-2 py-1 text-[13.5px]'
export const labelCls = 'flex flex-col gap-0.5 text-[12px] text-eq-neutral'
export const primaryBtn = (disabled: boolean) =>
  [
    'rounded-eq-sm px-3 py-1.5 font-heading text-[12.5px] font-bold',
    disabled ? 'cursor-not-allowed bg-eq-grey text-eq-helper' : 'bg-eq-navy text-white',
  ].join(' ')
export const quietBtn = 'rounded-eq-sm border border-eq-navy px-3 py-1 font-heading text-[12px] font-bold text-eq-navy'

/** Today in South Africa, as YYYY-MM-DD -- the server's "today" too. */
/** Integer cents as "R 1 250.00". */
export const rand = (cents: number): string =>
  `${cents < 0 ? '-' : ''}R ${Math.floor(Math.abs(cents) / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}.${String(Math.abs(cents) % 100).padStart(2, '0')}`

const STATUS_STYLE: Record<string, string> = {
  draft: 'bg-eq-neutral-bg text-eq-neutral',
  submitted: 'bg-eq-ice text-eq-deep',
  approved: 'bg-eq-success-bg text-eq-success',
  returned: 'bg-eq-warning-bg text-eq-warning',
}

/** Where an entry or disbursement stands (T&B-1 PR 3), in a small chip. */
export function StatusChip({ status }: { status: string }) {
  return (
    <span className={`rounded-eq-pill px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[status] ?? STATUS_STYLE.draft}`}>
      {status}
    </span>
  )
}

export function todaySast(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Johannesburg', year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date()
  )
}

/** "1001 · Name"; the number is left out where the caller does not have it (0). */
export const clientLabel = (c: ClientRef | null): string =>
  c === null ? 'No client' : c.clientNumber > 0 ? `${c.clientNumber} · ${c.shortName}` : c.shortName

/** Every billing job, fetched once per mount. */
export function useBillingJobs(): { jobs: BillingJob[] | null; error: string | null } {
  const [jobs, setJobs] = useState<BillingJob[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    api
      .billingJobs()
      .then((r) => live && setJobs(r.jobs))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)))
    return () => {
      live = false
    }
  }, [])
  return { jobs, error }
}

/** Search the register and pick one client. */
export function ClientPicker({
  value,
  onChange,
  disabled,
  allowNone,
}: {
  value: ClientRef | null
  onChange: (c: ClientRef | null) => void
  disabled?: boolean
  allowNone?: boolean
}) {
  const [q, setQ] = useState('')
  const [rows, setRows] = useState<ClientListRow[]>([])
  useEffect(() => {
    if (disabled) return
    let live = true
    const t = setTimeout(() => {
      api
        .clients({ q: q || null, status: 'active' })
        .then((r) => live && setRows(r.clients.slice(0, 40)))
        .catch(() => live && setRows([]))
    }, 200)
    return () => {
      live = false
      clearTimeout(t)
    }
  }, [q, disabled])

  if (disabled) {
    return <div className="py-1 text-[13.5px]">{clientLabel(value)}</div>
  }
  const options = value !== null && !rows.some((r) => r.id === value.id) ? [value, ...rows] : rows
  return (
    <span className="flex gap-1">
      <input className={`${inputCls} w-32`} placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search clients" />
      <select
        className={`${inputCls} w-56`}
        value={value?.id ?? ''}
        aria-label="Client"
        onChange={(e) => {
          const id = e.target.value
          if (id === '') return onChange(null)
          const r = options.find((o) => o.id === id)!
          onChange({ id: r.id, clientNumber: r.clientNumber, shortName: r.shortName })
        }}
      >
        <option value="">{allowNone ? 'No client' : '—'}</option>
        {options.map((c) => (
          <option key={c.id} value={c.id}>
            {c.clientNumber} · {c.shortName}
          </option>
        ))}
      </select>
    </span>
  )
}

/** A billing job and, if it has any, one of its tasks. */
export function JobTaskPicker({
  jobs,
  jobId,
  taskId,
  onChange,
  include,
}: {
  jobs: BillingJob[]
  jobId: string
  taskId: string | null
  onChange: (jobId: string, taskId: string | null) => void
  /** Which kinds to offer. */
  include: 'work' | 'leave' | 'all'
}) {
  const offered = jobs.filter((j) => (!j.archived || j.id === jobId) && (include === 'all' || j.kind === include))
  const job = jobs.find((j) => j.id === jobId)
  const tasks = (job?.tasks ?? []).filter((t) => !t.archived || t.id === taskId)
  return (
    <>
      <label className={labelCls}>
        Billing job
        <select className={`${inputCls} w-56`} value={jobId} onChange={(e) => onChange(e.target.value, null)}>
          <option value="">—</option>
          {offered.map((j) => (
            <option key={j.id} value={j.id}>
              {j.name}
              {j.kind === 'leave' ? ' (leave)' : j.chargeable ? '' : ' (not chargeable)'}
            </option>
          ))}
        </select>
      </label>
      {tasks.length > 0 && (
        <label className={labelCls}>
          Task (optional)
          <select className={`${inputCls} w-36`} value={taskId ?? ''} onChange={(e) => onChange(jobId, e.target.value || null)}>
            <option value="">—</option>
            {tasks.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
      )}
    </>
  )
}

export interface EntryDraft {
  workDate: string
  hours: string
  jobId: string
  taskId: string | null
  client: ClientRef | null
  narrative: string
}

export function draftFromEntry(e: TimeEntryRow): EntryDraft {
  return {
    workDate: e.workDate,
    hours: e.hours,
    jobId: e.job.id,
    taskId: e.task?.id ?? null,
    client: e.client,
    narrative: e.narrative ?? '',
  }
}

export function bodyFromDraft(d: EntryDraft, jobs: BillingJob[]): EntryBody {
  const job = jobs.find((j) => j.id === d.jobId)
  return {
    workDate: d.workDate,
    hours: d.hours,
    jobId: d.jobId,
    taskId: d.taskId,
    // Leave never has a client; sending one would only earn a refusal.
    clientId: job?.kind === 'leave' ? null : (d.client?.id ?? null),
    narrative: d.narrative,
  }
}

/** The fields of one entry. Leave hides the client. */
export function EntryFields({
  draft,
  setDraft,
  jobs,
  clientFixed,
  dates,
  include = 'all',
}: {
  draft: EntryDraft
  setDraft: (d: EntryDraft) => void
  jobs: BillingJob[]
  clientFixed?: boolean
  /** Restrict the date to these days (My week); otherwise a free date. */
  dates?: string[]
  include?: 'work' | 'leave' | 'all'
}) {
  const job = jobs.find((j) => j.id === draft.jobId)
  return (
    <>
      <label className={labelCls}>
        Date
        {dates ? (
          <select className={inputCls} value={draft.workDate} onChange={(e) => setDraft({ ...draft, workDate: e.target.value })}>
            {dates.map((d) => (
              <option key={d} value={d}>
                {new Date(`${d}T00:00:00`).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })}
              </option>
            ))}
          </select>
        ) : (
          <input type="date" className={inputCls} value={draft.workDate} onChange={(e) => setDraft({ ...draft, workDate: e.target.value })} />
        )}
      </label>
      <label className={labelCls}>
        Hours
        <input
          className={`${inputCls} w-20 text-right`}
          value={draft.hours}
          inputMode="decimal"
          placeholder="0.25"
          onChange={(e) => setDraft({ ...draft, hours: e.target.value })}
        />
      </label>
      <JobTaskPicker
        jobs={jobs}
        jobId={draft.jobId}
        taskId={draft.taskId}
        include={include}
        onChange={(jobId, taskId) => setDraft({ ...draft, jobId, taskId })}
      />
      {job?.kind !== 'leave' && (
        <label className={labelCls}>
          Client{job && !job.chargeable ? ' (optional)' : ''}
          <ClientPicker value={draft.client} onChange={(client) => setDraft({ ...draft, client })} disabled={clientFixed} allowNone={job ? !job.chargeable : false} />
        </label>
      )}
      <label className={labelCls}>
        Narrative
        <input className={`${inputCls} w-64`} value={draft.narrative} onChange={(e) => setDraft({ ...draft, narrative: e.target.value })} maxLength={2000} />
      </label>
    </>
  )
}

/**
 * Log time: a quick add from a client page or a due-list row. The client is
 * fixed; the billing job is prefilled when one is linked to the row's
 * obligation type.
 */
export function LogTimeDialog({
  client,
  obligationTypeCode,
  narrative,
  onClose,
  onSaved,
}: {
  client: ClientRef
  obligationTypeCode?: string | null
  narrative?: string
  onClose: () => void
  onSaved: (e: TimeEntryRow) => void
}) {
  const { jobs, error: jobsError } = useBillingJobs()
  const [draft, setDraft] = useState<EntryDraft>({
    workDate: todaySast(),
    hours: '',
    jobId: '',
    taskId: null,
    client,
    narrative: narrative ?? '',
  })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const prefilled = useRef(false)

  useEffect(() => {
    if (jobs === null || prefilled.current) return
    prefilled.current = true
    const linked = jobs.find((j) => !j.archived && j.kind === 'work' && obligationTypeCode && j.obligationTypeCode === obligationTypeCode)
    if (linked) setDraft((d) => ({ ...d, jobId: linked.id }))
  }, [jobs, obligationTypeCode])

  const ready = draft.jobId !== '' && draft.hours.trim() !== ''

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(10,22,40,.35)] pt-24" role="dialog" aria-modal="true" aria-label="Log time">
      <div className="w-[min(46rem,94vw)] rounded-eq-md border border-eq-border bg-white px-5 py-4 shadow-eq-card">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="m-0 text-[18px]">Log time — {clientLabel(client)}</h2>
          <button className="text-[13px] underline" onClick={onClose}>
            Close
          </button>
        </div>
        {jobsError && <p className="text-eq-danger">{jobsError}</p>}
        {jobs === null ? (
          <p className="text-[12.5px] text-eq-helper">Loading billing jobs…</p>
        ) : jobs.filter((j) => !j.archived && j.kind === 'work').length === 0 ? (
          <p className="text-[14px]">No billing jobs yet. A manager adds them under Jobs and prices.</p>
        ) : (
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault()
              if (busy) return
              setBusy(true)
              setError(null)
              api
                .createTimeEntry(bodyFromDraft(draft, jobs))
                .then(onSaved)
                .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
                .finally(() => setBusy(false))
            }}
          >
            <EntryFields draft={draft} setDraft={setDraft} jobs={jobs} clientFixed include="work" />
            <button type="submit" className={primaryBtn(busy || !ready)} disabled={busy || !ready}>
              Log time
            </button>
          </form>
        )}
        {error && <p className="mb-0 mt-3 text-[13.5px] text-eq-danger">{error}</p>}
        <p className="mb-0 mt-3 text-[12px] text-eq-helper">
          Decimal hours in steps of 0.05: 0.1 = 6 min, 0.25 = 15 min. The entry is a draft on your week until you submit it.
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The top-bar timer
// ---------------------------------------------------------------------------

const clock = (seconds: number): string => {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/**
 * One timer per person, held on the server: the start time survives a closed
 * tab and a second device. The ticking clock here is display only; what is
 * logged is worked out by the database when the timer stops.
 */
export function TimerBar() {
  const [available, setAvailable] = useState(false)
  const [timer, setTimer] = useState<TimerState | null>(null)
  const [fetchedAt, setFetchedAt] = useState(Date.now())
  const [now, setNow] = useState(Date.now())
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stopped, setStopped] = useState<TimeEntryRow | null>(null)
  const [busy, setBusy] = useState(false)

  const apply = (t: TimerState | null) => {
    setTimer(t)
    setFetchedAt(Date.now())
  }

  useEffect(() => {
    api
      .timer()
      .then((r) => {
        setAvailable(r.available)
        apply(r.timer)
      })
      .catch(() => setAvailable(false))
  }, [])

  useEffect(() => {
    if (timer === null || !timer.running) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [timer])

  if (!available) return null

  const act = (fn: () => Promise<unknown>) => {
    if (busy) return
    setBusy(true)
    setError(null)
    fn()
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false))
  }

  const seconds = timer === null ? 0 : timer.elapsedSeconds + (timer.running ? Math.max(0, Math.floor((now - fetchedAt) / 1000)) : 0)

  return (
    <div className="relative">
      {timer === null ? (
        <button className={quietBtn} onClick={() => (setOpen(!open), setStopped(null))}>
          ▶ Start timer
        </button>
      ) : (
        <div className="flex items-center gap-2 rounded-eq-sm border border-eq-border px-2 py-1">
          <span className={['font-heading text-[14px] font-bold tabular-nums', timer.running ? 'text-eq-navy' : 'text-eq-helper'].join(' ')}>
            {clock(seconds)}
          </span>
          <span className="max-w-[16rem] truncate text-[12.5px] text-eq-neutral" title={`${timer.job.name}${timer.client ? ' — ' + clientLabel(timer.client) : ''}`}>
            {timer.job.name}
            {timer.client && ` · ${timer.client.shortName}`}
          </span>
          {timer.running ? (
            <button className="text-[12.5px] underline" disabled={busy} onClick={() => act(() => api.pauseTimer().then((r) => apply(r.timer)))}>
              Pause
            </button>
          ) : (
            <button className="text-[12.5px] underline" disabled={busy} onClick={() => act(() => api.resumeTimer().then((r) => apply(r.timer)))}>
              Resume
            </button>
          )}
          <button
            className={quietBtn}
            disabled={busy}
            onClick={() =>
              act(() =>
                api.stopTimer().then((r) => {
                  apply(null)
                  setStopped(r.entry)
                })
              )
            }
          >
            Stop
          </button>
          <button
            className="text-[12px] text-eq-helper underline"
            disabled={busy}
            onClick={() => {
              if (window.confirm('Discard this timer? Nothing will be logged.')) act(() => api.discardTimer().then(() => apply(null)))
            }}
          >
            Discard
          </button>
        </div>
      )}

      {(open && timer === null) || error || stopped ? (
        <div className="absolute right-0 top-full z-40 mt-2 w-[min(40rem,90vw)] rounded-eq-md border border-eq-border bg-white px-4 py-3 shadow-eq-card">
          {stopped && (
            <p className="m-0 text-[13.5px]">
              Logged <b>{stopped.hours} h</b> on {stopped.job.name}
              {stopped.client && ` for ${stopped.client.shortName}`} as a draft.{' '}
              <Link to={`/time/my-week?start=${stopped.workDate}`} className="underline" onClick={() => setStopped(null)}>
                Open My week
              </Link>{' '}
              to change it.{' '}
              <button className="text-[12.5px] underline" onClick={() => setStopped(null)}>
                Close
              </button>
            </p>
          )}
          {error && <p className="m-0 text-[13.5px] text-eq-danger">{error}</p>}
          {open && timer === null && !stopped && (
            <StartTimerForm
              onStarted={(t) => {
                apply(t)
                setOpen(false)
              }}
              onCancel={() => setOpen(false)}
              setError={setError}
            />
          )}
        </div>
      ) : null}
    </div>
  )
}

function StartTimerForm({
  onStarted,
  onCancel,
  setError,
}: {
  onStarted: (t: TimerState) => void
  onCancel: () => void
  setError: (e: string | null) => void
}) {
  const { jobs } = useBillingJobs()
  const [jobId, setJobId] = useState('')
  const [taskId, setTaskId] = useState<string | null>(null)
  const [client, setClient] = useState<ClientRef | null>(null)
  const [narrative, setNarrative] = useState('')
  const [busy, setBusy] = useState(false)
  const job = useMemo(() => jobs?.find((j) => j.id === jobId), [jobs, jobId])
  if (jobs === null) return <p className="m-0 text-[12.5px] text-eq-helper">Loading…</p>
  const ready = jobId !== '' && (!job?.chargeable || client !== null)
  return (
    <form
      className="flex flex-wrap items-end gap-3"
      onSubmit={(e) => {
        e.preventDefault()
        if (busy) return
        setBusy(true)
        setError(null)
        api
          .startTimer({ jobId, taskId, clientId: client?.id ?? null, narrative })
          .then((r) => onStarted(r.timer))
          .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
          .finally(() => setBusy(false))
      }}
    >
      <JobTaskPicker jobs={jobs} jobId={jobId} taskId={taskId} include="work" onChange={(j, t) => (setJobId(j), setTaskId(t))} />
      <label className={labelCls}>
        Client{job && !job.chargeable ? ' (optional)' : ''}
        <ClientPicker value={client} onChange={setClient} allowNone={job ? !job.chargeable : false} />
      </label>
      <label className={labelCls}>
        Narrative
        <input className={`${inputCls} w-56`} value={narrative} onChange={(e) => setNarrative(e.target.value)} />
      </label>
      <button type="submit" className={primaryBtn(busy || !ready)} disabled={busy || !ready}>
        Start
      </button>
      <button type="button" className="text-[12.5px] underline" onClick={onCancel}>
        Cancel
      </button>
    </form>
  )
}
